/* eslint-disable import/prefer-default-export */
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AccountPersistInfo, ApiInfo, Subscription } from 'Internal.CommonAPI';
import {
  getHighestActivePaidSubscription,
  getCustomerDisplayUser,
  getIsShorePayUser,
  getIsTrialUser,
  getActiveSubscriptions,
  getActiveSubscriptionNames,
  getGiftcardsV2User,
  getIsBetaUser,
  getIsInternalBetaUser,
  getIsTipsUser,
} from 'Account/selectors';
import { useLocalStorageState } from 'ahooks';
import { useIntl } from 'react-intl';
import config from 'Datev/config';
import { useCommonApi } from 'utils/apiClient/services/common';
import { handleErrorNotification } from '../utils';

export const useCommonInfo = (userToken?: string) => {
  const queryClient = useQueryClient();
  const intl = useIntl();
  // eslint-disable-next-line no-unused-vars ,  @typescript-eslint/no-unused-vars
  const [account, setAccount] =
    useLocalStorageState<AccountPersistInfo>('account');
  const Common = useCommonApi(userToken || undefined);
  return useQuery({
    queryKey: ['common', 'api'],
    queryFn: async () => {
      try {
        const response: ApiInfo = await Common.getInfo(userToken);
        if (!response?.account) {
          // eslint-disable-next-line no-console
          console.error('Invalid API response: missing account data', response);
          handleErrorNotification({
            error: 'Invalid API response structure',
            lang: intl.locale.toLowerCase(),
          });
          throw new Error('Invalid API response structure');
        }

        const subscriptions = response.account.subscriptions || [];
        const permissions = response.account.permissions || [];

        queryClient.setQueryData(
          ['account', 'isActive'],
          permissions.includes('subscriptions.can_use_inventorum')
        );
        queryClient.setQueryData(
          ['account', 'activeSubscriptions'],
          subscriptions.filter((subs) => subs.active)
        );

        queryClient.setQueryData(['account', 'permissions'], permissions);
        queryClient.setQueryData(
          ['account', 'isPlusUser'],
          getCustomerDisplayUser(permissions)
        );
        queryClient.setQueryData(
          ['account', 'country'],
          response.account.country
        );

        queryClient.setQueryData(
          ['account', 'source'],
          response.account.source
        );
        queryClient.setQueryData(
          ['account', 'first_owner_id'],
          response.account.first_owner_id
        );
        queryClient.setQueryData(
          ['account', 'isTrialUser'],
          getIsTrialUser(subscriptions)
        );

        queryClient.setQueryData(['account', 'email'], response.account.email);

        queryClient.setQueryData(['account', 'id'], response.account.id);
        queryClient.setQueryData(
          ['account', 'signUpDate'],
          response.account.account_creation
        );
        queryClient.setQueryData(
          ['account', 'merchant_id'],
          response.account.external_id
        );
        queryClient.setQueryData(
          ['account', 'activeSubscriptions'],
          getActiveSubscriptions(subscriptions)
        );

        const highestActivePaidSub =
          getHighestActivePaidSubscription(subscriptions);

        queryClient.setQueryData(
          ['account', 'highestActiveSubscription'],
          highestActivePaidSub
        );

        let data;
        try {
          data = {
            email: response.account.email,
            signUpDate: response.account.account_creation,
            currency: response.account.currency,
            firstOwnerId: response.account.first_owner_id,
            merchant_id: response.account.external_id || undefined,
            userSuccessful: true,
            accountSuccessful: true,
            country: response.account.country,
            source: response.account.source || '',
            isActive: permissions.includes('subscriptions.can_use_inventorum'),
            id: response.account.id,
            isPlusUser: getCustomerDisplayUser(permissions),
            isShorePayUser: getIsShorePayUser(permissions),
            isTrialUser: getIsTrialUser(subscriptions),
            isBetaUser: getIsBetaUser(permissions),
            isTipsUser: getIsTipsUser(permissions),
            isInternalBetaUser: getIsInternalBetaUser(subscriptions),
            hasDatevSub: getActiveSubscriptionNames(subscriptions).includes(
              config.datevSubscription
            ),
            hasGiftcardsV2Sub: getGiftcardsV2User(permissions),
            tokenSetAt: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            activeSubscriptions: getActiveSubscriptions(subscriptions),
            highestActiveSubscription: highestActivePaidSub,
            ...(userToken !== undefined && { userToken }),
          };
        } catch (dataError) {
          // eslint-disable-next-line no-console
          console.error('Error building account data object:', {
            error: dataError,
            subscriptions,
            permissions,
            account: response.account,
          });
          handleErrorNotification({
            error: (dataError as Error).message,
            lang: intl.locale.toLowerCase(),
          });
          throw dataError;
        }

        try {
          setAccount((prev) => ({ ...prev, ...data } as AccountPersistInfo));
        } catch (setAccountError) {
          // eslint-disable-next-line no-console
          console.error('setAccount failed with error:', {
            error: setAccountError,
            errorMessage: (setAccountError as Error).message,
            errorStack: (setAccountError as Error).stack,
          });
          // eslint-disable-next-line no-console
          console.warn(
            'Stripping subscription fields from localStorage due to serialization failure'
          );
          try {
            const {
              highestActiveSubscription: _highestActiveSubscription,
              activeSubscriptions: _activeSubscriptions,
              ...simpleData
            } = data;
            setAccount(
              (prev) => ({ ...prev, ...simpleData } as AccountPersistInfo)
            );
          } catch (retryError) {
            // eslint-disable-next-line no-console
            console.error('Retry also failed:', retryError);
            throw retryError;
          }
        }

        const activeSubscriptions: Subscription[] | undefined =
          queryClient.getQueryData(['account', 'activeSubscriptions']);
        if (activeSubscriptions !== undefined) {
          queryClient.setQueryData(
            ['account', 'activeSubscriptionNames'],
            activeSubscriptions.map((as) => as.subscription_plan.name)
          );

          const activeSubscriptionNames: string[] | undefined =
            queryClient.getQueryData(['account', 'activeSubscriptionNames']);
          if (activeSubscriptionNames !== undefined) {
            queryClient.setQueryData(
              ['account', 'isInternalBetaUser'],
              activeSubscriptionNames.includes('Internal Beta Access')
            );
          }
        }
        return response;
      } catch (error) {
        handleErrorNotification({
          error: (error as Error).message,
          lang: intl.locale.toLowerCase(),
        });
        return null;
      }
    },
    staleTime: 1500,
    enabled: false,
  });
};
